import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Plug, CheckCircle2, XCircle, Loader2, RefreshCw, Settings, Send,
  Zap, Shield, ShoppingCart, Users, AlertTriangle, Clock, Eye, EyeOff,
  ExternalLink, Trash2, Play, History
} from 'lucide-react';

const INTEGRATION_ICONS = {
  slack: '💬', google_sheets: '📊', airtable: '📋', salesforce: '☁️',
};
const INTEGRATION_COLORS = {
  slack: 'border-purple-300 bg-purple-50', google_sheets: 'border-emerald-300 bg-emerald-50',
  airtable: 'border-blue-300 bg-blue-50', salesforce: 'border-sky-300 bg-sky-50',
};

export default function IntegrationHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Integrations');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInt, setSelectedInt] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [enabledEvents, setEnabledEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [triggerEvent, setTriggerEvent] = useState('');
  const [triggerData, setTriggerData] = useState('{"title":"Test Event","message":"Testing integration from TechOps"}');
  const [triggering, setTriggering] = useState(false);

  const loadIntegrations = useCallback(async () => {
    try {
      const { data } = await api.get('/api/integrations/available');
      setIntegrations(data.integrations || []);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadIntegrations(); }, [loadIntegrations]);

  const openConfig = async (intId) => {
    setSelectedInt(intId);
    try {
      const { data } = await api.get(`/api/integrations/config/${intId}`);
      setConfigData(data);
      setCredentials(data.config?.credentials || {});
      setEnabledEvents(data.config?.enabled_events || []);
    } catch { toast.error('Failed to load config'); }
  };

  const saveConfig = async () => {
    if (!selectedInt) return;
    setSaving(true);
    try {
      await api.put(`/api/integrations/config/${selectedInt}`, {
        enabled: true,
        credentials,
        enabled_events: enabledEvents,
      });
      toast.success('Integration saved');
      loadIntegrations();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const testConnection = async () => {
    if (!selectedInt) return;
    setTesting(true);
    try {
      const { data } = await api.post(`/api/integrations/test/${selectedInt}`);
      if (data.success) toast.success(data.detail || 'Connection successful!');
      else toast.error(data.detail || 'Connection failed');
      loadIntegrations();
    } catch { toast.error('Test failed'); } finally { setTesting(false); }
  };

  const disconnectInt = async (intId) => {
    if (!window.confirm('Disconnect this integration? Credentials will be removed.')) return;
    try {
      await api.delete(`/api/integrations/config/${intId}`);
      toast.success('Disconnected');
      setSelectedInt(null);
      setConfigData(null);
      loadIntegrations();
    } catch { toast.error('Disconnect failed'); }
  };

  const loadLogs = async (intId) => {
    try {
      const { data } = await api.get(`/api/integrations/logs?integration_id=${intId || ''}&limit=30`);
      setLogs(data.logs || []);
      setShowLogs(true);
    } catch (e) { /* handled */ }
  };

  const fireEvent = async () => {
    if (!triggerEvent) { toast.error('Select an event type'); return; }
    setTriggering(true);
    try {
      let data = {};
      try { data = JSON.parse(triggerData); } catch (e) { /* handled */ }
      const { data: result } = await api.post('/api/integrations/trigger', { event_type: triggerEvent, data });
      const succeeded = result.results?.filter(r => r.success).length || 0;
      const total = result.results?.length || 0;
      toast.success(`Event dispatched: ${succeeded}/${total} integrations received`);
      if (showLogs) loadLogs('');
    } catch { toast.error('Trigger failed'); } finally { setTriggering(false); }
  };

  const toggleEvent = (evt) => {
    setEnabledEvents(prev => prev.includes(evt) ? prev.filter(e => e !== evt) : [...prev, evt]);
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  const schema = selectedInt ? configData?.schema : null;

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="integration-hub">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><Plug className="w-6 h-6 text-[#D4A843]" /> Integration Hub</h1>
              <p className="text-sm text-gray-500 mt-1">Connect TechOps to your business tools — Slack, Sheets, Airtable, Salesforce</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => loadLogs('')} data-testid="view-all-logs"><History className="w-3.5 h-3.5 mr-1" /> All Logs</Button>
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={loadIntegrations}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
            </div>
          </div>

          {/* Integration Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="integration-cards">
            {integrations.map(int_ => (
              <div key={int_.id} className={`border-2 rounded-lg p-5 transition-all cursor-pointer hover-lift ${int_.connected ? INTEGRATION_COLORS[int_.id] : 'border-gray-200 bg-white'} ${selectedInt === int_.id ? 'ring-2 ring-[#D4A843]' : ''}`} onClick={() => openConfig(int_.id)} data-testid={`integration-${int_.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl">{INTEGRATION_ICONS[int_.id]}</span>
                  {int_.connected ? (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" /> Connected</span>
                  ) : (
                    <span className="text-[10px] text-gray-400">Not connected</span>
                  )}
                </div>
                <h3 className="text-sm font-bold text-gray-900">{int_.name}</h3>
                <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{int_.description}</p>
                {int_.enabled_events?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">{int_.enabled_events.slice(0, 3).map(e => (
                    <span key={e} className="px-1.5 py-0.5 bg-white/70 rounded text-[9px] text-gray-600">{e.replace(/_/g, ' ')}</span>
                  ))}{int_.enabled_events.length > 3 && <span className="text-[9px] text-gray-400">+{int_.enabled_events.length - 3}</span>}</div>
                )}
                {int_.last_sync && <p className="text-[9px] text-gray-400 mt-2">Last: {new Date(int_.last_sync).toLocaleString()}</p>}
              </div>
            ))}
          </div>

          {/* Configuration Panel */}
          {selectedInt && schema && (
            <div className="bg-white border-2 border-[#D4A843]/20 rounded-lg p-6 animate-fade-in" data-testid="integration-config">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-xl">{INTEGRATION_ICONS[selectedInt]}</span>
                  {schema.name} Configuration
                </h2>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => loadLogs(selectedInt)} className="text-[10px]"><History className="w-3 h-3 mr-0.5" /> Logs</Button>
                  <Button size="sm" variant="ghost" className="text-red-400 text-[10px]" onClick={() => disconnectInt(selectedInt)} data-testid="disconnect-btn"><Trash2 className="w-3 h-3 mr-0.5" /> Disconnect</Button>
                  <button onClick={() => { setSelectedInt(null); setConfigData(null); }} className="text-gray-400 hover:text-gray-600"><XCircle className="w-5 h-5" /></button>
                </div>
              </div>

              {/* Credential Fields */}
              <div className="space-y-3 mb-5">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Credentials</h3>
                {schema.fields.map(field => (
                  <div key={field.key}>
                    <label className="text-xs text-gray-600">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea value={credentials[field.key] || ''} onChange={e => setCredentials({...credentials, [field.key]: e.target.value})} className="mt-1 w-full h-24 rounded-lg border border-gray-200 px-3 py-2 text-xs font-mono bg-gray-50" placeholder={field.placeholder} data-testid={`cred-${field.key}`} />
                    ) : (
                      <Input value={credentials[field.key] || ''} onChange={e => setCredentials({...credentials, [field.key]: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" type={field.type === 'password' ? 'password' : 'text'} placeholder={field.placeholder} data-testid={`cred-${field.key}`} />
                    )}
                  </div>
                ))}
              </div>

              {/* Event Subscriptions */}
              <div className="mb-5">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Event Subscriptions</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {schema.events.map(evt => (
                    <label key={evt} className="flex items-center gap-2 text-xs text-gray-700 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <input type="checkbox" checked={enabledEvents.includes(evt)} onChange={() => toggleEvent(evt)} className="rounded" data-testid={`event-${evt}`} />
                      {evt.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-100">
                <Button onClick={saveConfig} disabled={saving} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="save-integration">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Save & Enable
                </Button>
                <Button onClick={testConnection} disabled={testing} variant="outline" className="rounded-lg text-xs" data-testid="test-integration">
                  {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Test Connection
                </Button>
              </div>
            </div>
          )}

          {/* Manual Event Trigger */}
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="event-trigger">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Play className="w-3.5 h-3.5 text-[#D4A843]" /> Test Event Trigger</h3>
            <p className="text-[10px] text-gray-500 mb-3">Fire a test event to all enabled integrations.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10px] text-gray-500">Event Type</label>
                <select value={triggerEvent} onChange={e => setTriggerEvent(e.target.value)} className="mt-0.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="trigger-event-type">
                  <option value="">Select event...</option>
                  <option value="new_order">New Order</option>
                  <option value="escalation">Escalation</option>
                  <option value="sla_breach">SLA Breach</option>
                  <option value="ticket_resolved">Ticket Resolved</option>
                  <option value="security_alert">Security Alert</option>
                  <option value="new_contact">New Contact</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500">Event Data (JSON)</label>
                <Input value={triggerData} onChange={e => setTriggerData(e.target.value)} className="mt-0.5 rounded-lg font-mono text-xs" placeholder='{"title":"...","message":"..."}' data-testid="trigger-data" />
              </div>
            </div>
            <Button onClick={fireEvent} disabled={triggering} variant="outline" className="rounded-lg text-xs" data-testid="fire-event-btn">
              {triggering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Fire Event
            </Button>
          </div>

          {/* Logs Panel */}
          {showLogs && (
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="integration-logs">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Activity Log</h3>
                <button onClick={() => setShowLogs(false)} className="text-gray-400 hover:text-gray-600"><XCircle className="w-4 h-4" /></button>
              </div>
              {logs.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">{logs.map(l => (
                  <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      {l.success ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                      <span className="text-[10px] font-bold text-gray-500">{INTEGRATION_ICONS[l.integration_id]} {l.integration_id}</span>
                      <span className="text-[10px] text-gray-600">{l.event?.replace(/_/g, ' ')}</span>
                      <span className="text-[10px] text-gray-400 truncate max-w-xs">{l.detail}</span>
                    </div>
                    <span className="text-[9px] text-gray-400 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</span>
                  </div>
                ))}</div>
              ) : <p className="text-center py-4 text-[10px] text-gray-400">No activity yet</p>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
